import { z } from 'zod';

// =============================================================================
//  ExternalPlan schemas — Story 2.4
//  Catálogo de planos individuais por SaaS (Reservei: Growth/Pro; OwlTask: Sight/Insight).
//  Serão referenciados pelos planos comerciais bundle (Story 2.5).
// =============================================================================

export const externalPlanStatusSchema = z.enum(['ativo', 'oculto', 'legado', 'descontinuado']);
export type ExternalPlanStatusSchema = z.infer<typeof externalPlanStatusSchema>;

const SLUG_REGEX = /^[a-z0-9_-]+$/;
const slugField = z
  .string()
  .min(1, 'Slug obrigatório.')
  .max(60, 'Slug deve ter até 60 caracteres.')
  .regex(SLUG_REGEX, 'Slug aceita só minúsculas, números, hífen e underscore.');

const metadataField = z
  .record(z.unknown())
  .optional()
  .nullable()
  .transform((v) => v ?? {});

const mutableBase = z.object({
  name: z.string().min(1, 'Nome obrigatório.').max(100, 'Nome deve ter até 100 caracteres.'),
  description: z
    .string()
    .max(1000, 'Descrição deve ter até 1000 caracteres.')
    .optional()
    .nullable(),
  status: externalPlanStatusSchema.default('ativo'),
  metadata: metadataField,
});

/**
 * Create — exige saas_product_id e slug (ambos imutáveis após criação).
 */
export const createExternalPlanSchema = mutableBase.extend({
  saas_product_id: z.string().uuid('saas_product_id inválido.'),
  slug: slugField,
});
export type CreateExternalPlanDto = z.output<typeof createExternalPlanSchema>;
export type CreateExternalPlanInput = z.input<typeof createExternalPlanSchema>;

/**
 * Update — partial. `saas_product_id` é imutável (não está no shape).
 * `slug` pode ser alterado (no MVP admin tem essa liberdade); valida regex.
 */
export const updateExternalPlanSchema = mutableBase
  .extend({
    slug: slugField.optional(),
  })
  .partial()
  .strict()
  .refine((data) => !('saas_product_id' in data), {
    message: 'saas_product_id é imutável após criação.',
    path: ['saas_product_id'],
  });
export type UpdateExternalPlanDto = z.output<typeof updateExternalPlanSchema>;
export type UpdateExternalPlanInput = z.input<typeof updateExternalPlanSchema>;

/**
 * Query — GET /api/external-plans
 */
export const queryExternalPlansSchema = z.object({
  saas_product_id: z.string().uuid().optional(),
  status: externalPlanStatusSchema.optional(),
  q: z.string().min(1).max(200).optional(),
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QueryExternalPlansDto = z.infer<typeof queryExternalPlansSchema>;
